// react
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";

// prop-types
import PropTypes from "prop-types";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TableFooter,
  TablePagination,
  IconButton,
  useMediaQuery,
  useTheme,
  TableSortLabel,
  Tooltip,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon,
  KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon,
  ManageSearch as ManageSearchIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";

// record-controller
import fetchPaginatedRecordsWithTypes from "record-controller/fetchPaginatedRecordsWithTypes";
import fetchSignature from "record-controller/fetchSignature";

// utils
import convertTimestamp from "utils/convertTimestamp";

// firebase
import { onSnapshot, query, collection, orderBy as orderByQuery, where } from "firebase/firestore";
import { db } from "config/firebase";

// A ---------------------------------------------------------------------- M

const AggregatedTableHead = ({ order, orderBy, onRequestSort }) => {
  const { t } = useTranslation();

  const headCells = useMemo(
    () => [
      { id: "id", label: t("record_id") },
      { id: "timestamp", label: t("generation_datetime") },
      { id: "type", label: t("type") },
      { id: "value", label: t("value") },
      { id: "actions", label: t("actions") },
    ],
    [t]
  );

  const createSortHandler = (property) => () => {
    if (property === "timestamp") onRequestSort(property);
  };

  return (
    <TableHead sx={{ backgroundColor: "grey.100" }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align={headCell.id !== "actions" ? "left" : "right"}>
            {headCell.id === "timestamp" ? (
              <TableSortLabel active={orderBy === headCell.id} direction={orderBy === headCell.id ? order : "asc"} onClick={createSortHandler(headCell.id)}>
                {headCell.label}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

AggregatedTableHead.propTypes = {
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  onRequestSort: PropTypes.func.isRequired,
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";

  const handleBackButtonClick = (event) => onPageChange(event, page - 1);
  const handleNextButtonClick = (event) => onPageChange(event, page + 1);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {isRtl ? <KeyboardArrowRightOutlinedIcon /> : <KeyboardArrowLeftOutlinedIcon />}
      </IconButton>
      <IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {isRtl ? <KeyboardArrowLeftOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const AggregatedTable = ({ tag, isTagGroupMember, checkVerification, handleView, handleFileClick }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("timestamp");
  const [order, setOrder] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const recordCacheRef = useRef({});
  const lastDocCacheRef = useRef({});
  const pageRef = useRef(page);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const fetchData = useCallback(async () => {
    if (recordCacheRef.current[pageRef.current]) {
      setRecords(recordCacheRef.current[pageRef.current]);
      return;
    }

    setIsLoading(true);

    try {
      const lastDoc = pageRef.current > 0 ? lastDocCacheRef.current[pageRef.current - 1] : null;
      const { records: newRecords, lastDoc: newLastDoc } = await fetchPaginatedRecordsWithTypes(tag.id, ["info", "doc", "img", "like_sirti"], isTagGroupMember, lastDoc, rowsPerPage, orderBy, order);

      recordCacheRef.current[pageRef.current] = newRecords;
      lastDocCacheRef.current[pageRef.current] = newLastDoc;

      setRecords(newRecords);
    } catch (error) {
      console.error("Error fetching paginated records:", error);
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  }, [tag.id, isTagGroupMember, page, rowsPerPage, orderBy, order]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!tag.id) return;

    const collectionRef = collection(db, "tagsdata", tag.id, "signatures");
    const q = query(collectionRef, where("type", "in", ["info", "doc", "img", "like_sirti"]), where("timestamp", ">=", Date.now().toString()), orderByQuery("timestamp", "desc"));

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      try {
        const recordPromises = snapshot.docs.map((doc) => fetchSignature(doc.id));
        const detailedRecords = await Promise.all(recordPromises);
        const newRecords = detailedRecords.filter((record) => record !== null);

        if (newRecords.length === 0) return;

        if (pageRef.current === 0) {
          recordCacheRef.current = {};
          lastDocCacheRef.current = {};
          fetchData();
        } else {
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Error processing new records:", error);
      }
    });

    return () => unsubscribe();
  }, [tag.id, isTagGroupMember, rowsPerPage, orderBy, order]);

  const handleChangePage = (e, newPage) => {
    pageRef.current = newPage;
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(+e.target.value);
    setPage(0);
    recordCacheRef.current = {};
    lastDocCacheRef.current = {};
  };

  const handleRequestSort = (prop) => {
    const isAsc = orderBy === prop && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(prop);
    setPage(0);
    recordCacheRef.current = {};
    lastDocCacheRef.current = {};
  };

  const handleReload = () => {
    recordCacheRef.current = {};
    lastDocCacheRef.current = {};
    setPage(0);
    fetchData();
    setSnackbarOpen(false);
  };

  return isLoading ? (
    <Box position="absolute" inset={0} display="flex" justifyContent="center" alignItems="center" bgcolor="rgba(255,255,255,0.8)" zIndex={10}>
      <CircularProgress />
    </Box>
  ) : (
    <>
      <TableContainer>
        <Table size="small">
          <AggregatedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
          <TableBody>
            {records.map((record) => {
              let type;

              if (record.type === "img") type = t("image");
              else if (record.type === "doc") type = t("document");
              else if (record.type === "info") type = t("note");
              else if (record.type === "like_sirti") type = "like_sirti";
              else if (record.type === "source_telemetrics") type = "source_telemetrics";
              else type = t("custom");

              return (
                <TableRow key={record.id}>
                  <TableCell align="left">{record.id || <span style={{ color: "gray" }}>N/A</span>}</TableCell>
                  <TableCell align="left">{record.timestamp ? convertTimestamp(record.timestamp) : record.type === "like_sirti" && record.data.ts ? record.data.ts : ""}</TableCell>
                  <TableCell align="left">{type}</TableCell>
                  {(record.type === "doc" || record.type === "img") && <TableCell align="left">{record.data.fileName || ""}</TableCell>}
                  {record.type === "info" && <TableCell align="left">{record.data.text || ""}</TableCell>}
                  {record.type === "like_sirti" && <TableCell align="left">{""}</TableCell>}
                  <TableCell align="right">
                    <Tooltip title={t("details")} placement="top">
                      <IconButton color="primary" onClick={() => handleView(record)}>
                        <ManageSearchIcon />
                      </IconButton>
                    </Tooltip>
                    {record.id.startsWith("UNCERTIFIED_") ? null : (
                      <Tooltip title={t("verify")} placement="top">
                        <IconButton color="success" onClick={() => checkVerification(record)}>
                          <VerifiedIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          {!isMobile && (
            <TableFooter sx={{ backgroundColor: "grey.100" }}>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 20]}
                  count={records.length < rowsPerPage ? page * rowsPerPage + records.length : (page + 1) * rowsPerPage + 1}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={t("rows_per_page")}
                  labelDisplayedRows={({ from, to, count }) => {
                    const displayCount = records.length === rowsPerPage ? `${count}+` : count;
                    return t("displayed_rows", { from, to, count: displayCount });
                  }}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="info"
          action={
            <Button size="small" onClick={handleReload}>
              {t("reload")}
            </Button>
          }
        >
          {t("new_data_available")}
        </Alert>
      </Snackbar>
    </>
  );
};

AggregatedTable.propTypes = {
  tag: PropTypes.object.isRequired,
  isTagGroupMember: PropTypes.bool.isRequired,
  checkVerification: PropTypes.func.isRequired,
  handleView: PropTypes.func.isRequired,
};

export default AggregatedTable;
